// src/utils/apiHelper.js

/**
 * API Helper with Auto-Token Refresh
 * Automatically handles 401 responses by refreshing the token
 */

const API_BASE_URL = '/api/v1'; // Your API base URL

export const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };
};

export const getLoggedInUserId = () => {
  const userJson = localStorage.getItem('user');
  if (userJson) {
    try {
      const user = JSON.parse(userJson);
      return user.id;
    } catch (error) {
      console.error('Error parsing user:', error);
      return null;
    }
  }
  return null;
};

export const getLoggedInUser = () => {
  const userJson = localStorage.getItem('user');
  if (userJson) {
    try {
      return JSON.parse(userJson);
    } catch (error) {
      console.error('Error parsing user:', error);
      return null;
    }
  }
  return null;
};

/**
 * Refresh the access token using the refresh token (from httpOnly cookie)
 */
const refreshAccessToken = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send refresh token cookie
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();

    // Store new access token
    localStorage.setItem('accessToken', data.accessToken);

    return data.accessToken;
  } catch (error) {
    console.error('Token refresh error:', error);
    // Clear auth on refresh failure
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
    return null;
  }
};

/**
 * Main API fetch with auto-refresh on 401
 */
export const apiCall = async (endpoint, options = {}) => {
  let response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  // If 401, try to refresh token and retry
  if (response.status === 401) {
    console.log('🔄 Token expired, refreshing...');
    const newToken = await refreshAccessToken();

    if (newToken) {
      // Retry request with new token
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: getAuthHeaders(),
        credentials: 'include',
      });
    } else {
      throw new Error('Session expired. Please login again.');
    }
  }

  // Handle other errors
  if (!response.ok) {
    try {
      const errorData = await response.json();
      console.error('❌ API Error Response:', errorData);
      throw new Error(errorData.message || errorData.error || `API error: ${response.status}`);
    } catch (parseError) {
      // If response is not JSON, return status text
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
  }

  return response.json();
};

/**
 * Convenience methods for common HTTP verbs
 */

export const apiGet = (endpoint) => {
  return apiCall(endpoint, { method: 'GET' });
};

export const apiPost = (endpoint, data) => {
  return apiCall(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const apiPut = (endpoint, data) => {
  return apiCall(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const apiDelete = (endpoint) => {
  return apiCall(endpoint, { method: 'DELETE' });
};

/**
 * Example Usage:
 * 
 * // Get logged-in user's ID
 * const userId = getLoggedInUserId();
 * 
 * // Fetch sales with auto-refresh
 * const sales = await apiGet('/sales');
 * 
 * // Create sale with user ID
 * const newSale = await apiPost('/sales', {
 *   userId: getLoggedInUserId(),
 *   amount: 100,
 *   date: new Date(),
 * });
 * 
 * // Update cash management
 * const updated = await apiPut(`/cash/${userId}`, {
 *   balance: 5000,
 * });
 */